import { useLocation, useNavigate } from 'react-router-dom';
import {
  Home,
  Briefcase,
  CalendarDays,
  FileText,
  ClipboardList,
  FileSignature,
  ReceiptText,
  Receipt,
  Users,
  BookOpen,
  Package,
  Wallet,
  BarChart3,
  ShoppingCart,
  Settings,
  Plus,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button3D } from '@/components/ui/button-3d';
import { SignUpModal } from '@/components/modals/SignUpModal';
import { useCreateQuote } from '@/hooks/useCreateQuote';

/**
 * Menu lateral do desktop. Só é montado a partir de `lg` (ver `useIsDesktop`),
 * onde a `BottomNavigation` sai de cena.
 *
 * ⚠️ AS ROTAS SÃO BILÍNGUES (#56): os módulos antigos estão em inglês
 * (`/quotes`, `/clients`, `/catalog`, `/reports`, `/settings`) e os novos em
 * português (`/ordens-servico`, `/contratos`, `/recibos`, `/estoque`,
 * `/financeiro`, `/agenda`, `/venda`, `/trabalhos`). Escrever `/orcamentos`
 * "por coerência" compila, não gera aviso e cai no catch-all como 404. Cada
 * destino abaixo foi conferido contra o `<Routes>` de `src/App.tsx`, e há guard
 * de teste afirmando isso.
 *
 * Não há portão de plano/papel aqui, de propósito: a barra inferior também não
 * tem, e quem decide o acesso é a tela de destino (que ordena papel antes de
 * plano, #38). Um item escondido aqui esconderia o módulo inteiro de quem
 * poderia assiná-lo.
 */
type NavItem = { label: string; route: string; icon: typeof Home };
type NavGroup = { title: string | null; items: NavItem[] };

const NAV_GROUPS: NavGroup[] = [
  {
    title: null,
    items: [
      { label: 'Início', route: '/dashboard', icon: Home },
      { label: 'Trabalhos', route: '/trabalhos', icon: Briefcase },
      { label: 'Agenda', route: '/agenda', icon: CalendarDays },
      { label: 'Venda rápida', route: '/venda', icon: ShoppingCart },
    ],
  },
  {
    title: 'Documentos',
    items: [
      { label: 'Orçamentos', route: '/quotes', icon: FileText },
      { label: 'Ordens de serviço', route: '/ordens-servico', icon: ClipboardList },
      { label: 'Contratos', route: '/contratos', icon: FileSignature },
      { label: 'Recibos', route: '/recibos', icon: ReceiptText },
      { label: 'Nota fiscal', route: '/nfse', icon: Receipt },
    ],
  },
  {
    title: 'Cadastros',
    items: [
      { label: 'Clientes', route: '/clients', icon: Users },
      { label: 'Catálogo', route: '/catalog', icon: BookOpen },
      { label: 'Estoque', route: '/estoque', icon: Package },
    ],
  },
  {
    title: 'Gestão',
    items: [
      { label: 'Financeiro', route: '/financeiro', icon: Wallet },
      { label: 'Relatórios', route: '/reports', icon: BarChart3 },
      { label: 'Ajustes', route: '/settings', icon: Settings },
    ],
  },
];

/** Todas as rotas declaradas acima, para o guard de rota do #56. */
export const DESKTOP_SIDEBAR_ROUTES = NAV_GROUPS.flatMap((g) => g.items.map((i) => i.route));

/**
 * O destino casa com a URL corrente. `startsWith` com a barra é o que impede
 * `/quotes` de acender junto com `/quotes-algo`, e a home é comparada por
 * igualdade porque `/dashboard` não tem filhos.
 */
function isActive(pathname: string, route: string): boolean {
  if (route === '/dashboard') return pathname === '/dashboard' || pathname === '/';
  return pathname === route || pathname.startsWith(`${route}/`);
}

export function DesktopSidebar() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // Papel → cota → plano → visitante: a MESMA regra do "+" da barra inferior,
  // porque é o mesmo gesto (ver `useCreateQuote`).
  const {
    createQuote,
    signUpOpen,
    setSignUpOpen,
  } = useCreateQuote();

  return (
    <>
      <aside
        aria-label="Navegação principal"
        className="fixed inset-y-0 left-0 z-40 flex w-64 flex-col border-r border-border/60 bg-card"
      >
        <div className="flex justify-center px-5 pb-4 pt-6">
          <button onClick={() => navigate('/dashboard')} className="block" aria-label="Ir para o início">
            {/* `alt` preenchido é o que mantém o nome acessível do botão depois
                que o texto virou imagem — sem ele o leitor de tela anuncia só
                "botão" (#142). */}
            <img
              src="https://kizuizwbhlttcmgmllst.supabase.co/storage/v1/object/public/brand-assets/logo-orcamigo.png"
              alt="Orçamigo"
              /* 173px = 144 + 20%. Cabe nos 216px úteis da barra (256 menos o
                 padding lateral), então o logo não encosta nas bordas. */
              className="h-auto w-[173px]"
              width={1536}
              height={585}
            />
          </button>
        </div>

        <div className="px-4 pb-4">
          <Button3D size="sm" className="w-full" onClick={createQuote}>
            <Plus className="mr-1.5 h-4 w-4" />
            Novo orçamento
          </Button3D>
        </div>

        <nav className="flex-1 space-y-5 overflow-y-auto px-3 pb-6">
          {NAV_GROUPS.map((group, groupIndex) => (
            <div key={group.title ?? `grupo-${groupIndex}`} className="space-y-1">
              {group.title && (
                <p className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {group.title}
                </p>
              )}
              {group.items.map((item) => {
                const Icon = item.icon;
                const active = isActive(pathname, item.route);
                return (
                  <button
                    key={item.route}
                    onClick={() => navigate(item.route)}
                    aria-current={active ? 'page' : undefined}
                    className={cn(
                      'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                      active
                        ? 'bg-primary/10 text-primary'
                        : 'text-foreground/70 hover:bg-muted hover:text-foreground',
                    )}
                  >
                    <Icon className="h-[18px] w-[18px] shrink-0" />
                    {/* `min-w-0` + quebra: "Ordens de serviço" é o rótulo mais
                        longo e cabe em uma linha nos 256px da barra; sem o
                        mínimo zero, um rótulo maior esticaria a coluna (#175). */}
                    <span className="min-w-0 break-words text-left">{item.label}</span>
                  </button>
                );
              })}
            </div>
          ))}
        </nav>
      </aside>

      <SignUpModal open={signUpOpen} onOpenChange={setSignUpOpen} />
    </>
  );
}
